/**
 * School-admin onboarding gate — the home surface a school admin sees while
 * their org's onboarding is in progress. The school IS linked (via org
 * membership) but no `SCHOOL_CAMPUS` location is provisioned yet, so the
 * normal school surfaces (Orders / Settings / Production Records) have nothing
 * to show. Instead of the "No schools linked" empty state, we greet the school
 * by name and funnel them into their onboarding tasks. Orders / Production
 * Records / Settings / Billing are disabled in the sidebar until the Ordo team
 * provisions the workspace (see `ordo-sidebar.tsx` + `OnboardingGateGuard`).
 *
 * Two phases share this surface: while tasks are open, "complete your
 * onboarding"; once every task is done but the team hasn't provisioned yet,
 * a "we're setting you up" waiting state.
 */

import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@ordo/ui/card'
import { Progress } from '@ordo/ui/progress'
import { CalendarClock, CheckCircle2, ListChecks } from 'lucide-react'
import { useNavigate } from 'react-router'

import { isAwaitingProvisioning, type SchoolOnboardingStatusSchool } from '@/api/school-onboarding'
import { OrdoPageContainer } from '@/components/ordo-page-container'

function formatDeadline(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

export function SchoolOnboardingHome({ school }: { school: SchoolOnboardingStatusSchool }) {
  const awaitingProvisioning = isAwaitingProvisioning(school)

  return (
    <OrdoPageContainer>
      <div className="mx-auto w-full max-w-2xl space-y-6 py-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">{school.organizationName}</h1>
          <p className="text-sm text-muted-foreground">
            {awaitingProvisioning
              ? 'Thanks for finishing onboarding — your workspace is almost ready.'
              : "Welcome to Ordo. Let's finish setting up your program."}
          </p>
        </div>

        {awaitingProvisioning ? <AwaitingProvisioningCard school={school} /> : <CompleteTasksCard school={school} />}
      </div>
    </OrdoPageContainer>
  )
}

/** Tasks still open — funnel the school into its checklist. */
function CompleteTasksCard({ school }: { school: SchoolOnboardingStatusSchool }) {
  const navigate = useNavigate()
  const completed = Math.max(0, school.totalTaskCount - school.openTaskCount)
  const percent = school.totalTaskCount > 0 ? Math.round((completed / school.totalTaskCount) * 100) : 0

  return (
    <Card>
      <CardHeader>
        <CardTitle>Complete your onboarding</CardTitle>
        <CardDescription>
          Orders, Production Records, and Settings unlock once the Ordo team provisions your workspace. Our team is
          working the same checklist alongside you.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">
              {completed} of {school.totalTaskCount} tasks complete
            </span>
            <span className="text-muted-foreground">{percent}%</span>
          </div>
          <Progress value={percent} />
        </div>

        {school.deadline ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <CalendarClock className="h-4 w-4" />
            <span>Onboarding deadline: {formatDeadline(school.deadline)}</span>
          </div>
        ) : null}

        <Button type="button" onClick={() => navigate('/onboarding')}>
          <ListChecks className="h-4 w-4" />
          Complete Onboarding Tasks
        </Button>
      </CardContent>
    </Card>
  )
}

/** All tasks done — waiting on the Ordo team to provision the workspace. */
function AwaitingProvisioningCard({ school }: { school: SchoolOnboardingStatusSchool }) {
  const navigate = useNavigate()

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-emerald-600" />
          <CardTitle>All onboarding tasks complete</CardTitle>
        </div>
        <CardDescription>
          The Ordo team is setting up {school.organizationName}&apos;s workspace. Orders, Production Records, and
          Settings will unlock automatically once setup is done — no action needed on your end.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">
              {school.totalTaskCount} of {school.totalTaskCount} tasks complete
            </span>
            <span className="text-muted-foreground">100%</span>
          </div>
          <Progress value={100} />
        </div>

        <Button type="button" variant="outline" onClick={() => navigate('/onboarding')}>
          <ListChecks className="h-4 w-4" />
          Review your submitted tasks
        </Button>
      </CardContent>
    </Card>
  )
}
